import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackView } from "../lib/api";
import SEO from "../components/SEO";
import {
  ArrowRight, Compass, Sparkles, Gauge, Shield, MessageCircle, Briefcase,
} from "lucide-react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.07, duration: 0.55, ease: [0.21, 0.47, 0.32, 0.98] },
  }),
};

const values = [
  {
    icon: Compass,
    title: "Stratégie d'abord",
    text: "Chaque projet démarre par une compréhension fine de votre activité, de votre audience et de vos objectifs. Le design suit, pas l'inverse.",
  },
  {
    icon: Sparkles,
    title: "Détail obsessionnel",
    text: "Typographies, espacements, micro-animations, transitions : c'est l'accumulation des détails qui distingue une marque premium d'une marque correcte.",
  },
  {
    icon: Gauge,
    title: "Performance native",
    text: "Sites optimisés Core Web Vitals dès la première ligne de code. Un design premium ne pardonne pas une page lente.",
  },
  {
    icon: Shield,
    title: "Engagement durable",
    text: "Nous restons disponibles après la livraison. Formation, ajustements, conseils SEO : votre projet vit au-delà de la mise en ligne.",
  },
];

const principles = [
  "Un seul interlocuteur dédié, de la première discussion à la mise en ligne.",
  "Livrables vectoriels et fichiers sources fournis pour chaque identité visuelle.",
  "Console de gestion incluse — vous gardez le contrôle de votre contenu.",
  "Optimisation SEO intégrée nativement, pas en option payante.",
  "Devis transparent, sans surprises ni frais cachés.",
];

export default function About() {
  useEffect(() => { trackView("/a-propos"); }, []);

  return (
    <div className="pt-32 pb-24 bg-[#09090b] text-white min-h-screen">
      <SEO
        title="À propos — Blackline Studio"
        description="Studio de création digitale spécialisé dans les identités visuelles et solutions web pour les entreprises ambitieuses. Notre approche, nos valeurs, notre méthode."
        keywords="à propos Blackline Studio, agence web premium, méthode, valeurs studio digital"
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">

        {/* HERO */}
        <div className="grid lg:grid-cols-12 gap-10 mb-24" data-testid="about-hero">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">À propos</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] mb-8" data-testid="about-title">
              Un studio, une <span className="italic font-light">obsession</span> :<br />
              la qualité <span className="gold-text">durable</span>.
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-zinc-300 text-lg leading-relaxed font-light mb-5">
              Blackline Studio est un studio de création digitale qui conçoit des sites
              internet, des identités visuelles et des solutions métier pour les entreprises
              qui refusent de se contenter du standard.
            </p>
            <p className="text-zinc-400 leading-relaxed font-light">
              Nous travaillons en cycles courts, sans intermédiaires, avec un niveau d'exigence
              emprunté aux meilleures agences — accessible aux commerces locaux comme aux
              marques émergentes.
            </p>
          </div>
        </div>

        {/* MANIFESTO band */}
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true }}
          variants={fadeUp}
          className="border-y border-white/10 py-12 mb-24"
          data-testid="manifesto"
        >
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.2] max-w-4xl">
            « Un site web n'est pas une dépense. C'est <span className="text-[#d4af37]">un outil
            commercial</span> qui doit travailler pour vous chaque jour, 24h sur 24. »
          </p>
        </motion.div>

        {/* VALUES */}
        <section className="mb-24" data-testid="values-section">
          <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Nos valeurs</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-12">
            Quatre principes <span className="italic font-light">non-négociables</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
                custom={i} variants={fadeUp}
                className="bg-[#0a0a0c] p-8 lg:p-10 card-lift hover:bg-[#101013]"
                data-testid={`value-${i}`}
              >
                <v.icon size={26} className="text-[#d4af37] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3 tracking-tight">{v.title}</h3>
                <p className="text-zinc-400 leading-relaxed font-light">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* APPROACH */}
        <section className="grid lg:grid-cols-12 gap-10 mb-24" data-testid="approach">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Notre approche</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Pensé pour <span className="italic font-light">durer</span>, conçu pour <span className="italic font-light">convertir</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <p className="text-zinc-300 leading-relaxed font-light mb-6">
              Nous n'appliquons pas de templates. Chaque projet commence par une page blanche
              et une discussion. Nous cherchons d'abord à comprendre ce qui rend votre
              activité différente — puis nous traduisons cette singularité en design.
            </p>
            <p className="text-zinc-400 leading-relaxed font-light">
              Côté technique, nous utilisons les standards les plus exigeants : architecture
              SEO native, performance Core Web Vitals, responsive parfait sur mobile et tablette,
              accessibilité, sécurité. Tout est mesuré, rien n'est laissé au hasard.
            </p>
          </div>
        </section>

        {/* PRINCIPLES list */}
        <section className="mb-24" data-testid="principles">
          <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Ce qui est inclus</div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mb-10">
            Le standard <span className="italic font-light">Blackline</span>.
          </h2>
          <ul className="divide-y divide-white/10 border-y border-white/10">
            {principles.map((p, i) => (
              <motion.li
                key={i}
                initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.6 }}
                custom={i} variants={fadeUp}
                className="flex items-start gap-6 py-6"
                data-testid={`principle-${i}`}
              >
                <span className="font-display text-2xl text-[#d4af37]/50 w-10 shrink-0">0{i+1}</span>
                <span className="text-zinc-300 text-lg font-light leading-relaxed">{p}</span>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* FOR WHO */}
        <section className="grid lg:grid-cols-12 gap-10 mb-24" data-testid="for-who">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.2em] text-[#d4af37] uppercase mb-4">Pour qui</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
              Nous travaillons avec...
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-white/10">
            {[
              { i: Briefcase, t: "Commerçants & artisans", d: "Boulangeries, fleuristes, barbers, fitness, immobilier — toute activité locale ambitieuse." },
              { i: Sparkles, t: "Marques émergentes", d: "Lancement de marque, refonte d'identité, repositionnement premium." },
              { i: Shield, t: "Professions libérales", d: "Avocats, médecins, consultants — un site qui inspire confiance dès la première seconde." },
              { i: Gauge, t: "Entreprises en croissance", d: "Refonte d'un site existant devenu trop étroit pour vos ambitions." },
            ].map((c, i) => (
              <div key={c.t} className="bg-[#0a0a0c] p-7" data-testid={`for-who-${i}`}>
                <c.i size={22} className="text-[#d4af37] mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-xl mb-2">{c.t}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed font-light">{c.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="border border-white/10 p-10 lg:p-14 text-center" data-testid="about-cta">
          <h2 className="font-display text-3xl sm:text-4xl mb-4 tracking-tight">
            Parlons de votre <span className="italic font-light">projet</span>.
          </h2>
          <p className="text-zinc-400 font-light mb-8 max-w-lg mx-auto">
            Échangeons 30 minutes pour comprendre vos objectifs. Devis gratuit sous 24 heures.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/devis" data-testid="about-cta-devis" className="btn-gold px-7 py-3.5 inline-flex items-center justify-center gap-2 text-sm">
              Demander un devis <ArrowRight size={16} />
            </Link>
            <Link to="/realisations" data-testid="about-cta-realisations" className="border border-white/15 hover:border-[#d4af37] px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 transition-colors">
              Voir nos réalisations
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
